<h1 mat-dialog-title>
  <span transloco="EXPORT_DOCUMENTS"></span>
</h1>
<mat-dialog-content>
  <mat-horizontal-stepper linear #stepper>
    <mat-step label="Select an export mode">
      <mat-radio-group
        [value]="exportMode()"
        (change)="onExportModeChange($event)"
      >
        <mat-radio-button [value]="exportModeValues.Documents"
          ><span transloco="DOCUMENTS"></span
        ></mat-radio-button>
        <br />
        @if (supportsExportDataOnly()) {
          <mat-radio-button
            [disabled]="disableExportData"
            [value]="exportModeValues.Data"
            ><span transloco="FIELD_DATA"></span
          ></mat-radio-button>
        }
        <br />
        <mat-radio-button
          [disabled]="disableExportData"
          [value]="exportModeValues.DocumentsAndData"
          ><span transloco="DOCUMENTS_AND_FIELD_DATA"></span
        ></mat-radio-button>
      </mat-radio-group>
    </mat-step>
    @if (includeDocuments()) {
      <mat-step [stepControl]="settingsForm" label="Select file options">
        <form [formGroup]="settingsForm">
          <div class="first-stepper-control">
            <mat-form-field class="full-width-form-field">
              <mat-label transloco="EXPORTED_FILENAME"></mat-label>
              <app-mention-form-field
                [mentionFormControl]="exportedFilenameControl"
                placeholder="Exported filename template. (Optional)"
                mentionTriggerChar="<"
                mentionReplacementStartChar="<"
                mentionReplacementEndChar=">"
                [mentions]="filenameArchiveFields"
                mentionDisplayKey="name"
              ></app-mention-form-field>
              @if (hasFilenameHistory()) {
                <button
                  mat-icon-button
                  matSuffix
                  [matMenuTriggerFor]="filenameHistoryMenu"
                >
                  <mat-icon>history</mat-icon>
                </button>
              }
              <mat-menu #filenameHistoryMenu="matMenu">
                <ng-template matMenuContent>
                  @for (filename of filenameHistory(); track filename) {
                    <button
                      mat-menu-item
                      (click)="exportedFilenameControl.setValue(filename)"
                    >
                      {{ filename }}
                    </button>
                  }
                </ng-template>
              </mat-menu>
              <mat-hint>
                {{ 'EXPORT_FILENAME_HINT' | transloco }}
              </mat-hint>
            </mat-form-field>
          </div>
          <div id="fileOptionsContainer">
            <p>
              <mat-checkbox formControlName="convertToPdf">
                <span transloco="CONVERT_PDF"></span>
              </mat-checkbox>
            </p>
            <p>
              <mat-checkbox formControlName="convertToBlackAndWhite"
                ><span transloco="CONVERT_TO_BLACKWHITE"></span>
              </mat-checkbox>
            </p>
            <p>
              <mat-checkbox formControlName="includeAnnotations">{{
                'INCLUDE_ANNOTATIONS' | transloco
              }}</mat-checkbox>
            </p>
          </div>
        </form>
      </mat-step>
    }
    @if (includeData()) {
      <mat-step
        [stepControl]="csvSettingsForm"
        label="Select field data options"
      >
        <form [formGroup]="csvSettingsForm">
          <div>
            <mat-form-field class="full-width-form-field">
              <mat-label transloco="CSV_FILENAME"></mat-label>
              <input
                matInput
                placeholder="field-data.csv"
                formControlName="filename"
              />
            </mat-form-field>
          </div>
          <mat-form-field>
            <mat-label transloco="DELIMITER"></mat-label>
            <input matInput placeholder="," formControlName="columnDelimiter" />
          </mat-form-field>
          <mat-form-field class="csv-options-field">
            <mat-label transloco="STRING_DELIMITER"></mat-label>
            <input matInput placeholder='"' formControlName="stringDelimiter" />
          </mat-form-field>
          <div>
            <mat-checkbox formControlName="includeHeader">{{
              'LITERAL_INCLUDE_HEADERS' | transloco
            }}</mat-checkbox>
            <mat-checkbox
              class="include-all-fields-checkbox"
              formControlName="includeAllFields"
            >
              {{ 'INCLUDE_ALL_FIELDS' | transloco }}
            </mat-checkbox>
          </div>
          @if (supportsExportFullTableData()) {
            <div>
              <mat-checkbox formControlName="includeFullTableData">
                <span transloco="INCLUDE_FULL_TABLE_DATA"></span>
              </mat-checkbox>
              <mat-icon
                class="checkbox-info-icon"
                [matTooltip]="'INCLUDE_FULL_TABLE_DATA_INFO_MSG' | transloco"
                >info</mat-icon
              >
            </div>
          }
          <div>
            @if (!csvSettingsControls.includeAllFields.value) {
              <mat-form-field id="fieldSelect" class="full-width-form-field">
                <mat-label>Exported Fields</mat-label>
                <mat-chip-grid
                  #fieldsChipList
                  cdkDropList
                  cdkDropListOrientation="horizontal"
                  (cdkDropListDropped)="onDropSelectedField($event)"
                >
                  @for (field of selectedFields; track field) {
                    <mat-chip-row
                      [value]="field"
                      cdkDrag
                      (removed)="removeField(field)"
                    >
                      {{ field.name }}
                      <mat-icon matChipRemove>cancel</mat-icon>
                    </mat-chip-row>
                  }
                  <input
                    [placeholder]="'FIELDS' | transloco"
                    #fieldsInput
                    [formControl]="selectedFieldsChipForm"
                    [matAutocomplete]="auto"
                    [matChipInputFor]="fieldsChipList"
                    [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                    (matChipInputTokenEnd)="addField($event)"
                  />
                </mat-chip-grid>
                <mat-autocomplete
                  #auto="matAutocomplete"
                  (optionSelected)="selectedField($event)"
                >
                  @for (field of filteredArchiveFields$ | async; track field) {
                    <mat-option [value]="field">
                      {{ field.name }}
                    </mat-option>
                  }
                </mat-autocomplete>
              </mat-form-field>
            }
          </div>
        </form>
      </mat-step>
    }
  </mat-horizontal-stepper>
  @if (exportInProgress) {
    <div>
      <h4>{{ 'EXPORTING_DOCUMENTS' | transloco }}...</h4>
      <mat-progress-bar
        mode="determinate"
        [value]="$safeNavigationMigration(exportStatus?.progress)"
      ></mat-progress-bar>
    </div>
  }
  @if (downloadingExportZip) {
    <div>
      <h4>{{ 'DOWNLOADING_EXPORT_ZIP' | transloco }}...</h4>
      <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    </div>
  }
</mat-dialog-content>
<mat-dialog-actions>
  <button mat-button mat-dialog-close transloco="CANCEL"></button>
  <span class="spacer"></span>
  @if (stepper.selectedIndex > 0) {
    <button mat-button (click)="stepper.previous()" transloco="BACK"></button>
  }

  @if (stepper.steps.length > 0) {
    @if (stepper.selectedIndex < stepper.steps.last.index()) {
      <button mat-button (click)="stepper.next()" transloco="NEXT"></button>
    }
    @if (stepper.selectedIndex === stepper.steps.last.index()) {
      <button
        mat-button
        [disabled]="disableExport"
        (click)="onClickExport()"
        transloco="EXPORT"
      ></button>
    }
  }
</mat-dialog-actions>
